import { debounce } from "../utils/debounce";
import luaCompletions from "./lua_completions.json";

export class CodeEditor {
  constructor(element, options) {
    this.element = element;
    this.options = options;
    this.EDITOR_DEBOUNCE = 500;
    this.view = null;
    this.debouncedOnChange = null;
  }

  async initialize() {
    const [
      { EditorView, basicSetup },
      { StreamLanguage },
      { lua },
      { xml },
      { dracula },
      { quietlight },
      { autocompletion },
      { keymap },
      { defaultKeymap, historyKeymap, indentWithTab },
      { Compartment },
    ] = await Promise.all([
      import("codemirror"),
      import("@codemirror/language"),
      import("@codemirror/legacy-modes/mode/lua"),
      import("@codemirror/lang-xml"),
      import("@uiw/codemirror-theme-dracula"),
      import("@uiw/codemirror-theme-quietlight"),
      import("@codemirror/autocomplete"),
      import("@codemirror/view"),
      import("@codemirror/commands"),
      import("@codemirror/state"),
    ]);

    const languageSupport = {
      lua: () => StreamLanguage.define(lua),
      xml: () => xml(),
    };

    const themes = {
      dark: dracula,
      light: quietlight,
    };

    // Keep the theme in its own compartment so it can be reconfigured at
    // runtime (e.g. when the user toggles light/dark) without rebuilding the
    // whole editor. See setTheme/1.
    this.themes = themes;
    this.themeCompartment = new Compartment();

    const createLuaCompletions = (context) => {
      let word = context.matchBefore(/[\w.]*/);
      if (word.from == word.to && !context.explicit) return null;
      return {
        from: word.from,
        options: luaCompletions,
      };
    };

    const languageCompletions = {
      lua: [createLuaCompletions],
      xml: [/* provided by @codemirror/lang-xml */],
    };

    const customKeyMap = [
      {
        key: "Mod-Enter",
        run: () => {
          document.dispatchEvent(new CustomEvent("executedFromCodeEditor"));
          return true;
        },
      },
    ];

    const createExtensions = () => {
      const selectedLangSupport = languageSupport[this.options.language] || languageSupport.lua;
      const selectedCompletions =
        languageCompletions[this.options.language] || languageCompletions.lua;

      return [
        keymap.of([...customKeyMap, ...defaultKeymap, ...historyKeymap, indentWithTab]),
        EditorView.editable.of(!this.options.disabled),
        this.themeCompartment.of(themes[this.options.theme] || dracula),
        selectedLangSupport(),
        autocompletion({ override: selectedCompletions }),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            this.debouncedOnChange(this.getContent());
          }
        }),
        basicSetup,
      ];
    };

    this.view = new EditorView({
      doc: this.options.initialContent,
      extensions: createExtensions(),
      parent: this.element,
    });

    this.EditorView = EditorView;
    this.view.dom.style.border = "none";
    this.view.dom.style.height = "100%";
    this.debouncedOnChange = debounce(this.options.onChange, this.EDITOR_DEBOUNCE);
  }

  jumpToLine(lineNumber) {
    if (!this.view || !Number.isInteger(lineNumber)) return;
    const n = Math.min(Math.max(lineNumber, 1), this.view.state.doc.lines);
    const line = this.view.state.doc.line(n);
    this.view.dispatch({
      selection: { anchor: line.from, head: line.from },
      effects: this.EditorView.scrollIntoView(line.from, { y: "center" }),
    });
    this.view.focus();
  }

  getContent() {
    return this.view ? this.view.state.doc.toString() : "";
  }

  setContent(content) {
    // Skip redundant replacements: a full-document replace collapses the
    // selection and resets the scroll position, so avoid it when the content
    // is already in sync (the common case for a read-only synced editor).
    if (this.view && content !== this.getContent()) {
      this.view.dispatch({
        changes: { from: 0, to: this.view.state.doc.length, insert: content },
      });
    }
  }

  setTheme(theme) {
    if (!this.view || !this.themeCompartment) return;
    this.view.dispatch({
      effects: this.themeCompartment.reconfigure(this.themes[theme] || this.themes.dark),
    });
  }

  destroy() {
    if (this.view) {
      this.view.destroy();
    }
  }
}
